import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef, NgZone, AfterViewInit, Self } from '@angular/core';
import { DragAndDropEvent, FileModel } from '../../typescript-angular-client/model/fileModel';
import { fromEvent } from 'rxjs';
import { FmTreeService } from '../services/fm-tree.service';
import { map, filter, debounceTime, exhaustMap, tap, takeUntil } from 'rxjs/operators';
import { FOLDER_KEY_FOR_FILESONLYMODE, NewFilesListService } from './files-list.service';
import { FolderTreeNode } from '../../typescript-angular-client/model/folderTreeNode';
import { NgOnDestroyService } from '../services/ng-on-destroy.service';
import { TreeDragDropService, TreeNode } from 'primeng/api';
import { ContextMenu} from 'primeng/contextmenu';
import { IsMultiFileModel, IsSingleFileModel } from '../utils';

@Component({
  selector: 'files-list',
  templateUrl: './files-list.html',
  styleUrls: ['files-list.scss'],
  providers: [NgOnDestroyService]
})
export class FilesListComponent implements OnInit, OnDestroy, AfterViewInit {
  public defaultImageSmall = '../assets/images/picItem.png';
  private _elements: FileModel[] = [];
  private _currentFolder: FolderTreeNode;
  private _dialogType: FileModel.FileTypeEnum;
  public draggedItem: FileModel | FileModel[] | TreeNode;

  @Input() set elements(value: FileModel[]) {
    this._elements = value;
  }

  get elements(): FileModel[] {
    return this._elements;
  }

  @Input('dialogType')
  set dialogType(value: FileModel.FileTypeEnum) {
    this._dialogType = value;
  }

  get dialogType(): FileModel.FileTypeEnum {
    return this._dialogType;
  }

  @Input('currentFolder')
  set currentFolder(value: FolderTreeNode) {
    this._currentFolder = value;
  }

  @Input() contextMenu: ContextMenu;
  @Input() isDataLoading: boolean;
  @Input() fileListPageSize: number;

  @Output() loadNext = new EventEmitter<string>();
  @Output() refresh = new EventEmitter<unknown>();
  @Output() search = new EventEmitter<unknown>();
  @Output() isSeveralFilesSelected = new EventEmitter<boolean>();
  @Output() itemContextMenuSelected = new EventEmitter();
  @Output() selectionChanged = new EventEmitter<unknown>();

  @Output() ItemSelected = new EventEmitter<unknown>();
  @Output() ItemUnselected = new EventEmitter<unknown>();
  @Output() itemDropped = new EventEmitter<DragAndDropEvent>();
  @Output() itemFromFolderThreeDropped = new EventEmitter<unknown>();
  @Output() moveToFolder = new EventEmitter<FileModel>();

  @ViewChild('scrolledDiv', {static: false}) scrolledDiv: ElementRef;

  selection: FileModel[];
  folderDrag: FolderTreeNode;

  constructor(
    private fmService: FmTreeService,
    private ddService: TreeDragDropService,
    private flService: NewFilesListService,
    private zone: NgZone,
    @Self() private ngOnDestroy$: NgOnDestroyService) {
  }

  ngOnInit() {
    document.getElementById('inputSearch').focus();

    this.fmService.onStartDrag()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((items) => this.draggedItem = items);

    this.fmService.onStoptDrag()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.draggedItem = null);

    this.ddService.dragStart$.pipe(
      filter((source) => source.tree !== null),
      map((source) => source.node),
      takeUntil(this.ngOnDestroy$)
    ).subscribe((item) => this.draggedItem = item);

    this.ddService.dragStop$.pipe(
      filter((source) => source.tree !== null),
      takeUntil(this.ngOnDestroy$)
    ).subscribe(() => this.draggedItem = null);
  }

  ngOnDestroy(): void {
    this.currentFolder = null;
    this.elements = null;
    this.draggedItem = null;
  }

  ngAfterViewInit(): void {
    fromEvent(this.scrolledDiv.nativeElement, 'scroll')
      // .pipe(tap(x => console.log('before filter')))
      .pipe(filter(() => this._currentFolder !== null && this._elements !== null))
      // .pipe(tap(x => console.log('after filter before map')))
      .pipe(map((d: UIEvent) => d.target))
      // .pipe(tap(x => console.log('after map before debounce')))
      .pipe(debounceTime(300))
      // .pipe(tap(x => console.log('after debounce before filter 2')))
      .pipe(filter((d: HTMLElement) => (d.offsetHeight + d.scrollTop) > (d.scrollHeight - 5)))
      // .pipe(tap(x => console.log('after filter 2 before filter 3')))
      .pipe(
        filter(() => {
          // console.log('this.elements.length: ', this.elements.length);
          const folderKey = this.flService.isFilesOnlyMode ? FOLDER_KEY_FOR_FILESONLYMODE : this._currentFolder.key;
          // console.log('this.flService.folderInfo[folderKey]: ', this.flService.folderInfo[folderKey]);
          return (this.elements.length < this.flService.folderInfo[folderKey]);
        }),
        // tap(x => console.log('after filter 3 before map')),
        map(() => Math.ceil(this.elements.length / this.fileListPageSize) + 1),
        // tap(x => console.log('after map before loadByPage')),
        tap(() => this.zone.run(() => {
          this.isDataLoading = true;
        })),
        exhaustMap((page) => {
          const folderKey = this.flService.isFilesOnlyMode ? FOLDER_KEY_FOR_FILESONLYMODE : this._currentFolder.key;
          return this.flService.loadByPage(folderKey, page, this.dialogType, this.fileListPageSize, (<HTMLInputElement>document.getElementById('inputSearch')).value);
        }),
        // tap(x => console.log('after map loadByPage')),
        takeUntil(this.ngOnDestroy$)
      )
      .subscribe({
        next: (r) => {
          this.zone.run(() => {
            this.elements = this.elements.concat(r.Files);
            this.isDataLoading = false;
          });
        }
      });
  }

  public trackItemsBy(item): string {
    if (item == null) {
      return;
    }
    return item.Id;
  }

  public startSearch = ($event): void => this.search.emit($event);

  public onElementRightClick = (event: MouseEvent, model: FileModel): void => {
    if (this.contextMenu) {
      const index = this.findIndexInSelection(model);
      const selected = (index >= 0);

      if (!selected) {
        this.selectionChanged.emit([model]);
      }

      this.contextMenu.show(event);
      this.itemContextMenuSelected.emit({ original: event, model: model });
    }
  }

  public onDrop = (event: DragEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    if (IsMultiFileModel(this.draggedItem)) {
      return;
    }

    if (IsSingleFileModel(this.draggedItem)) {
      const item = this.draggedItem as FileModel;
      if (!item.IsFolder || item.ParentFolderId === this._currentFolder.key) {
        return;
      }
    }

    const emitEntity: DragAndDropEvent = {
      originalEvent: event,
      draggedItem: this.draggedItem,
      droppedItem: this._currentFolder.data
    };

    if (this._currentFolder.key === '0') {
      emitEntity.droppedItem = this._currentFolder as FileModel;
      emitEntity.droppedItem.Id = '0';
      emitEntity.droppedItem.IsFolder = true;
    }

    this.itemDropped.emit(emitEntity);
  }

  public onElementClick = (event: MouseEvent, element: FileModel): void => {
    const index = this.findIndexInSelection(element);
    const selected = index >= 0;

    if (event.ctrlKey && this.dialogType === FileModel.FileTypeEnum.IMAGES) {
      if (selected) {
        this.selection = this.selection.filter((v, i) => i !== index);
        this.ItemUnselected.emit(element);
      } else {
        this.selection = this.selection || [];
        this.selection = [...this.selection, element];
        this.ItemSelected.emit(element);
      }
    } else {
      if (selected) {
        this.selection = [];
      } else {
        this.selection = [];
        this.selection = [element];
      }
    }
    this.selectionChanged.emit(this.selection);

  }

  public onElementDoubleClick = (event: MouseEvent, model: FileModel): void => {
    if (model && model.IsFolder) {
      event.preventDefault();
      event.stopPropagation();
      this.moveToFolder.emit(model);
    }
  }

  isSelected = (node: FileModel) => this.findIndexInSelection(node) >= 0;

  private findIndexInSelection(node: FileModel): number {
    let index = -1;
    if (this.selection) {
      const models = this.selection;
      for (let i = 0; i < models.length; i++) {
        const selectedNode = models[i];
        const areNodesEqual = (selectedNode.Id && selectedNode.Id === node.Id);
        if (areNodesEqual) {
          index = i;
          break;
        }
      }
    }
    return index;
  }
}
